import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CarePlanSectionProps, NA, NO, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  UNIVERSAL_PRECAUTIONS_GENIE_FIELD_DEFINITIONS,
  UNIVERSAL_PRECAUTIONS_GENIE_QUESTIONS,
  UniversalPrecautionsGenieSectionValues,
} from "./universalPrecautionsGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const UniversalPrecautionSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS,

    anyPrecautionsAlwaysApply: sectionData?.anyPrecautionsAlwaysApply || false,
    precautionsAlwaysApplyDetails: sectionData?.precautionsAlwaysApplyDetails || '',
    precautionsAlwaysApplyAssistanceRequired: sectionData?.precautionsAlwaysApplyAssistanceRequired || "",

    anySpecialPrecautions: sectionData?.anySpecialPrecautions || false,
    specialPrecautionsDetails: sectionData?.specialPrecautionsDetails || '',
    specialPrecautionsAssistanceRequired: sectionData?.specialPrecautionsAssistanceRequired || "",

    anyAlternativeMethodOfVisitation: sectionData?.anyAlternativeMethodOfVisitation || false,
    alternativeMethodOfVisitationDetails: sectionData?.alternativeMethodOfVisitationDetails || '',
    alternativeMethodOfVisitationAssistanceRequired: sectionData?.alternativeMethodOfVisitationAssistanceRequired || "",

    canWearMask: sectionData?.canWearMask || false,
    maskWearingDetails: sectionData?.maskWearingDetails || '',
    maskWearingAssistanceRequired: sectionData?.maskWearingAssistanceRequired || "",

    safeToLeaveSanitizerWithResident: sectionData?.safeToLeaveSanitizerWithResident || false,
    sanitizerDetails: sectionData?.sanitizerDetails || '',
    sanitizerAssistanceRequired: sectionData?.sanitizerAssistanceRequired || "",

    upToDateWithVaccinations: sectionData?.upToDateWithVaccinations || false,
    vaccinationsDetails: sectionData?.vaccinationsDetails || '',
    vaccinationsAssistanceRequired: sectionData?.vaccinationsAssistanceRequired || "",

    anySharedMedicalEquipment: sectionData?.anySharedMedicalEquipment || false,
    sharedMedicalEquipmentDetails: sectionData?.sharedMedicalEquipmentDetails || '',
    sharedMedicalEquipmentAssistanceRequired: sectionData?.sharedMedicalEquipmentAssistanceRequired || "",

    anyOtherPrecautions: sectionData?.anyOtherPrecautions || false,
    otherPrecautionsDetails: sectionData?.otherPrecautionsDetails || '',
    otherPrecautionsAssistanceRequired: sectionData?.otherPrecautionsAssistanceRequired || "",
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, getValues, setValue } = useForm({ defaultValues });
  const watchAnyPrecautionsAlwaysApply = watch('anyPrecautionsAlwaysApply', defaultValues.anyPrecautionsAlwaysApply);
  const watchAnySpecialPrecautions = watch('anySpecialPrecautions', defaultValues.anySpecialPrecautions);
  const watchAnyAlternativeMethodOfVisitation = watch('anyAlternativeMethodOfVisitation', defaultValues.anyAlternativeMethodOfVisitation);
  const watchSafeToLeaveSanitizerWithResident = watch('safeToLeaveSanitizerWithResident', defaultValues.safeToLeaveSanitizerWithResident);
  const watchAnySharedMedicalEquipment = watch('anySharedMedicalEquipment', defaultValues.anySharedMedicalEquipment);
  const watchAnyOtherPrecautions = watch('anyOtherPrecautions', defaultValues.anyOtherPrecautions);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={UNIVERSAL_PRECAUTIONS_GENIE_QUESTIONS}
        fieldDefinitions={UNIVERSAL_PRECAUTIONS_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as UniversalPrecautionsGenieSectionValues}
        successMessage="Universal precautions care plan updated."
        errorMessage="Unable to update the universal precautions care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = { ...getValues(), ...payload.valueUpdates };
          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });
          await onsubmit(nextValues);
        }}
      />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Precautions always applicable</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyPrecautionsAlwaysApply })}>Any Precautions that always apply:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPrecautionsAlwaysApply"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPrecautionsAlwaysApply')}
                    </div>

                    {
                      watchAnyPrecautionsAlwaysApply && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="precautionsAlwaysApplyDetails" className={classNames({ 'p-error': errors.precautionsAlwaysApplyDetails })}>
                                Add Precautions details that always apply:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="precautionsAlwaysApplyDetails" control={control}
                                rules={{ required: 'Precautions details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Precautions details that always apply"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('precautionsAlwaysApplyDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="precautionsAlwaysApplyAssistanceRequired" className={classNames({ 'p-error': errors.precautionsAlwaysApplyAssistanceRequired })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="precautionsAlwaysApplyAssistanceRequired" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for precautions that always apply"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('precautionsAlwaysApplyAssistanceRequired')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any precautions that always apply: </strong>{sectionData?.anyPrecautionsAlwaysApply ? YES : NO}</div>
                    {
                      sectionData?.anyPrecautionsAlwaysApply && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Always applicable precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.precautionsAlwaysApplyDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.precautionsAlwaysApplyAssistanceRequired || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Special precautions</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anySpecialPrecautions })}>Any Special Precautions:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anySpecialPrecautions"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anySpecialPrecautions')}
                    </div>

                    {
                      watchAnySpecialPrecautions && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="specialPrecautionsDetails" className={classNames({ 'p-error': errors.specialPrecautionsDetails })}>
                                Add Special Precautions details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.UNIVERSAL_SPECIAL_PRECAUTIONS) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="specialPrecautionsDetails" control={control}
                                rules={{ required: 'Precautions details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add special precautions details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('specialPrecautionsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="specialPrecautionsAssistanceRequired" className={classNames({ 'p-error': errors.specialPrecautionsAssistanceRequired })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="specialPrecautionsAssistanceRequired" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for special precautions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('specialPrecautionsAssistanceRequired')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special precautions: </strong>{sectionData?.anySpecialPrecautions ? YES : NO}</div>
                    {
                      sectionData?.anySpecialPrecautions && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Always applicable precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialPrecautionsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialPrecautionsAssistanceRequired || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Visitation</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyAlternativeMethodOfVisitation })}>Any Alternative Method of Visitation:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyAlternativeMethodOfVisitation"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyAlternativeMethodOfVisitation')}
                    </div>

                    {
                      watchAnyAlternativeMethodOfVisitation && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="alternativeMethodOfVisitationDetails" className={classNames({ 'p-error': errors.alternativeMethodOfVisitationDetails })}>
                                Add Alternative Method of Visitation details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="alternativeMethodOfVisitationDetails" control={control}
                                rules={{ required: 'Alternative Method of Visitation details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Alternative Method of Visitation details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('alternativeMethodOfVisitationDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="alternativeMethodOfVisitationAssistanceRequired" className={classNames({ 'p-error': errors.alternativeMethodOfVisitationAssistanceRequired })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="alternativeMethodOfVisitationAssistanceRequired" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for alternative method of visitation"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('alternativeMethodOfVisitationAssistanceRequired')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any alternative method of visitation: </strong>{sectionData?.anyAlternativeMethodOfVisitation ? YES : NO}</div>
                    {
                      sectionData?.anyAlternativeMethodOfVisitation && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Always applicable precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.alternativeMethodOfVisitationDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.alternativeMethodOfVisitationAssistanceRequired || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Mask</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.canWearMask })}>Resident can wear a mask:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="canWearMask"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('canWearMask')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="maskWearingDetails" className={classNames({ 'p-error': errors.maskWearingDetails })}>
                          Add details about mask wearing:
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="maskWearingDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Add mask wearing details"
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('maskWearingDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="maskWearingAssistanceRequired" className={classNames({ 'p-error': errors.maskWearingAssistanceRequired })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="maskWearingAssistanceRequired" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Describe any assistance required for mask wearing"
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('maskWearingAssistanceRequired')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident can wear a mask: </strong>{sectionData?.canWearMask ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Always applicable precautions: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.maskWearingDetails || NA)}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with precautions: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.maskWearingAssistanceRequired || NA)}</span>
                    </div>
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Sanitizer/Wipes precautions</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.safeToLeaveSanitizerWithResident })}>Resident is safe to have sanitizer or disinfectant wipes left out for caregiver and client use:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="safeToLeaveSanitizerWithResident"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('safeToLeaveSanitizerWithResident')}
                    </div>

                    {
                      watchSafeToLeaveSanitizerWithResident && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="sanitizerDetails" className={classNames({ 'p-error': errors.sanitizerDetails })}>
                                Add details about sanitizer/wipes precautions:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="sanitizerDetails" control={control}
                                rules={{ required: 'Sanitizer/wipes precautions details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add sanitizer/wipes precautions details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('sanitizerDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="sanitizerAssistanceRequired" className={classNames({ 'p-error': errors.sanitizerAssistanceRequired })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="sanitizerAssistanceRequired" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for sanitizer/wipes precautions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('sanitizerAssistanceRequired')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <strong>Resident is safe to have sanitizer or disinfectant wipes left out for caregiver and client use: </strong>
                      {sectionData?.safeToLeaveSanitizerWithResident ? YES : NO}</div>
                    {
                      sectionData?.safeToLeaveSanitizerWithResident && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Always applicable precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.sanitizerDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.sanitizerAssistanceRequired || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Vaccinations</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.upToDateWithVaccinations })}>Is resident vaccinations up to date?</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="upToDateWithVaccinations"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('upToDateWithVaccinations')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="vaccinationsDetails" className={classNames({ 'p-error': errors.vaccinationsDetails })}>
                          Add Vaccination details:
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.UNIVERSAL_PRECAUTIONS_VACCINATIONS) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="vaccinationsDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Add Vaccination details"
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('vaccinationsDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="vaccinationsAssistanceRequired" className={classNames({ 'p-error': errors.vaccinationsAssistanceRequired })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="vaccinationsAssistanceRequired" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Describe any assistance required for vaccinations"
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('vaccinationsAssistanceRequired')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Is resident vaccinations up to date? : </strong>{sectionData?.upToDateWithVaccinations ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Vaccination details: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.vaccinationsDetails || NA)}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with vaccinations: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.vaccinationsAssistanceRequired || NA)}</span>
                    </div>
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Medical equipments</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anySharedMedicalEquipment })}>Resident shares any medical equipment:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anySharedMedicalEquipment"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anySharedMedicalEquipment')}
                    </div>

                    {
                      watchAnySharedMedicalEquipment && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="sharedMedicalEquipmentDetails" className={classNames({ 'p-error': errors.sharedMedicalEquipmentDetails })}>
                                Add Shared Equipment details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="sharedMedicalEquipmentDetails" control={control}
                                rules={{ required: 'Shared Equipment details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Shared Equipment details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('sharedMedicalEquipmentDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="sharedMedicalEquipmentAssistanceRequired" className={classNames({ 'p-error': errors.sharedMedicalEquipmentAssistanceRequired })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="sharedMedicalEquipmentAssistanceRequired" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for shared medical equipment"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('sharedMedicalEquipmentAssistanceRequired')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident shares the following medical equipment: </strong>{sectionData?.anySharedMedicalEquipment ? YES : NO}</div>
                    {
                      sectionData?.anySharedMedicalEquipment && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Shared equipment: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.sharedMedicalEquipmentDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with shared equipment: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.sharedMedicalEquipmentAssistanceRequired || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Other precautions</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOtherPrecautions })}>Any other precautions:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOtherPrecautions"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOtherPrecautions')}
                    </div>

                    {
                      watchAnyOtherPrecautions && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherPrecautionsDetails" className={classNames({ 'p-error': errors.otherPrecautionsDetails })}>
                                Other precautions details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherPrecautionsDetails" control={control}
                                rules={{ required: 'Precautions details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Other precautions details that always apply"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherPrecautionsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherPrecautionsAssistanceRequired" className={classNames({ 'p-error': errors.otherPrecautionsAssistanceRequired })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherPrecautionsAssistanceRequired" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for other precautions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherPrecautionsAssistanceRequired')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other precautions: </strong>{sectionData?.anyOtherPrecautions ? YES : NO}</div>
                    {
                      sectionData?.anyOtherPrecautions && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Other applicable precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherPrecautionsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with precautions: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherPrecautionsAssistanceRequired || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default UniversalPrecautionSection;


